import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Autorenew as GenerateIcon,
  ContentCopy as CopyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { NO_AUTOFILL } from '../../utils/noAutofill';
import {
  MIN_PASSWORD_LENGTH,
  formatCredentials,
  generateInitialPassword,
  roleLabel,
  validateAccountForm
} from '../../utils/portalAccounts';

const emptyForm = {
  operatorId: '',
  name: '',
  email: '',
  phone: '',
  password: '',
  role: ''
};

const ROLE_OPTIONS = {
  operator: [
    { value: 'operator_admin', hint: 'Darf eigene Fahrer und Fahrzeuge anlegen und die eigenen Fahrten disponieren.' },
    { value: 'operator_staff', hint: 'Sieht die Daten des eigenen Unternehmens, darf aber keine Stammdaten anlegen.' }
  ],
  central: [
    { value: 'dispatcher', hint: 'Darf operativ disponieren.' },
    { value: 'admin', hint: 'Vollzugriff für die Hauptzentrale, inklusive Einstellungen und Konten.' }
  ]
};

/**
 * Portalzugang anlegen — für Unternehmen (`variant="operator"`) und für die
 * Hauptzentrale (`variant="central"`).
 *
 * Nach dem Anlegen bleibt der Dialog offen und zeigt die Zugangsdaten zum
 * Weitergeben. Vorher schloss er sofort: E-Mail und Initialpasswort waren damit
 * weg, und die E-Mail tauchte auch in der Übersicht nirgends wieder auf.
 */
const PortalAccountDialog = ({
  open,
  variant,
  operators = [],
  initialOperatorId = '',
  isSaving = false,
  error = null,
  onCreate,
  onClose
}) => {
  const isOperatorVariant = variant === 'operator';
  const defaultRole = isOperatorVariant ? 'operator_admin' : 'dispatcher';

  const [form, setForm] = useState({ ...emptyForm, role: defaultRole });
  const [touched, setTouched] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [created, setCreated] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    if (!open) return;
    setForm({ ...emptyForm, role: defaultRole, operatorId: initialOperatorId || '' });
    setTouched({});
    setShowPassword(false);
    setCreated(null);
    setCopiedField(null);
  }, [open, defaultRole, initialOperatorId]);

  const activeOperators = useMemo(
    () => operators.filter(operator => operator.active !== false),
    [operators]
  );
  // Inaktive Unternehmen bleiben wählbar (etwa um einen Zugang vorzubereiten),
  // werden aber als solche gekennzeichnet.
  const inactiveOperators = useMemo(
    () => operators.filter(operator => operator.active === false),
    [operators]
  );

  const { valid, errors } = validateAccountForm(form, { requireOperator: isOperatorVariant });
  const showError = (field) => Boolean(touched[field] && errors[field]);

  const setField = (field) => (event) => {
    setForm(prev => ({ ...prev, [field]: event.target.value }));
  };
  const markTouched = (field) => () => setTouched(prev => ({ ...prev, [field]: true }));

  const copy = async (label, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(label);
      window.setTimeout(() => setCopiedField(null), 2000);
    } catch {
      setCopiedField(null);
    }
  };

  const handleSubmit = async () => {
    setTouched({ operatorId: true, name: true, email: true, password: true });
    if (!valid) return;

    const result = await onCreate(form);
    if (!result?.ok) return;

    setCreated({
      companyName: isOperatorVariant
        ? operators.find(operator => operator.id === form.operatorId)?.name || ''
        : '',
      name: form.name.trim(),
      email: result.email || form.email.trim().toLowerCase(),
      password: form.password,
      role: form.role
    });
  };

  const title = isOperatorVariant ? 'Zugang für ein Unternehmen anlegen' : 'Konto der Hauptzentrale anlegen';
  const roleOptions = ROLE_OPTIONS[isOperatorVariant ? 'operator' : 'central'];
  const activeRoleHint = roleOptions.find(option => option.value === form.role)?.hint;

  const credentialRow = (label, value, isSecret = false) => (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="body2" sx={{ minWidth: 110, color: 'text.secondary' }}>{label}</Typography>
      <Typography
        variant="body2"
        sx={{ fontFamily: 'monospace', flexGrow: 1, wordBreak: 'break-all' }}
      >
        {isSecret && !showPassword ? '•'.repeat(value.length) : value}
      </Typography>
      {isSecret && (
        <Tooltip title={showPassword ? 'Verbergen' : 'Anzeigen'}>
          <IconButton size="small" onClick={() => setShowPassword(prev => !prev)}>
            {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title={copiedField === label ? 'Kopiert' : 'Kopieren'}>
        <IconButton size="small" onClick={() => copy(label, value)}>
          <CopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{created ? 'Zugang angelegt' : title}</DialogTitle>
      <DialogContent>
        {created ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="success">
              Der Zugang ist aktiv. Bitte geben Sie die Daten sicher weiter — das
              Passwort wird nicht gespeichert und kann später nicht erneut
              angezeigt, sondern nur neu gesetzt werden.
            </Alert>
            <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Stack spacing={1}>
                {created.companyName && credentialRow('Unternehmen', created.companyName)}
                {credentialRow('Name', created.name)}
                {credentialRow('E-Mail', created.email)}
                {credentialRow('Passwort', created.password, true)}
                {credentialRow('Rolle', roleLabel(created.role))}
              </Stack>
              <Divider sx={{ my: 1.5 }} />
              <Button
                size="small"
                startIcon={<CopyIcon />}
                onClick={() => copy('Alle', formatCredentials(created))}
              >
                {copiedField === 'Alle' ? 'Kopiert' : 'Alle Zugangsdaten kopieren'}
              </Button>
            </Box>
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            {isOperatorVariant && operators.length === 0 && (
              <Alert severity="info">
                Es ist noch kein Unternehmen angelegt. Legen Sie zuerst ein
                Unternehmen an — der Zugang wird immer einem Unternehmen zugeordnet.
              </Alert>
            )}

            {isOperatorVariant && operators.length > 0 && (
              <FormControl fullWidth required error={showError('operatorId')}>
                <InputLabel id="portal-account-operator-label">Unternehmen</InputLabel>
                <Select
                  labelId="portal-account-operator-label"
                  label="Unternehmen"
                  value={form.operatorId}
                  onChange={setField('operatorId')}
                  onBlur={markTouched('operatorId')}
                >
                  {activeOperators.map(operator => (
                    <MenuItem key={operator.id} value={operator.id}>{operator.name}</MenuItem>
                  ))}
                  {inactiveOperators.map(operator => (
                    <MenuItem key={operator.id} value={operator.id}>
                      {operator.name} (inaktiv)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              required
              fullWidth
              label="Name"
              value={form.name}
              onChange={setField('name')}
              onBlur={markTouched('name')}
              error={showError('name')}
              helperText={showError('name') ? errors.name : ' '}
              autoComplete={NO_AUTOFILL}
            />
            <TextField
              required
              fullWidth
              type="email"
              label="E-Mail"
              value={form.email}
              onChange={setField('email')}
              onBlur={markTouched('email')}
              error={showError('email')}
              helperText={showError('email') ? errors.email : 'Wird zugleich der Benutzername für die Anmeldung.'}
              autoComplete={NO_AUTOFILL}
            />
            <TextField
              fullWidth
              label="Telefon"
              value={form.phone}
              onChange={setField('phone')}
              autoComplete={NO_AUTOFILL}
            />
            <TextField
              required
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label="Initiales Passwort"
              value={form.password}
              onChange={setField('password')}
              onBlur={markTouched('password')}
              error={showError('password')}
              helperText={
                showError('password')
                  ? errors.password
                  : `Mindestens ${MIN_PASSWORD_LENGTH} Zeichen. Wird nach dem Anlegen zum Weitergeben angezeigt.`
              }
              autoComplete={NO_AUTOFILL}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={showPassword ? 'Verbergen' : 'Anzeigen'}>
                      <IconButton size="small" onClick={() => setShowPassword(prev => !prev)}>
                        {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Passwort vorschlagen">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setForm(prev => ({ ...prev, password: generateInitialPassword() }));
                          setShowPassword(true);
                          setTouched(prev => ({ ...prev, password: true }));
                        }}
                      >
                        <GenerateIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />

            <FormControl fullWidth>
              <InputLabel id="portal-account-role-label">Rolle</InputLabel>
              <Select
                labelId="portal-account-role-label"
                label="Rolle"
                value={form.role}
                onChange={setField('role')}
              >
                {roleOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>{roleLabel(option.value)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {activeRoleHint && <Alert severity="info">{activeRoleHint}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{created ? 'Schließen' : 'Abbrechen'}</Button>
        {!created && (
          <Button
            variant="contained"
            disabled={isSaving || !valid || (isOperatorVariant && operators.length === 0)}
            onClick={handleSubmit}
          >
            Anlegen
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PortalAccountDialog;
